/**
 * The Fidelen glyph set.
 *
 * Ported verbatim from the locked design system's specimen sheet — a 24×24
 * grid, stroke only, round cap and join, `currentColor`. The path data is the
 * design system's, not an approximation of it: an icon set redrawn by hand is a
 * second visual language, and the point of a specimen sheet is that there is
 * only one.
 *
 * Only the glyphs the product actually uses are carried. The sheet has more;
 * copying the unused ones would be shipping dead weight.
 *
 * `currentColor` throughout, so a glyph inherits whatever the row it sits in
 * has already decided — the sidebar's muted white, a heading's ink. No icon
 * names a colour.
 */
type Glyph = {
  /** Stroked paths. */
  d?: string[];
  /** Circles, as [cx, cy, r]. */
  c?: [number, number, number][];
  /** Rounded rects, as [x, y, w, h, r]. */
  r?: [number, number, number, number, number][];
  /** Stroke width, where the specimen overrides the 1.7 default. */
  w?: number;
};

export const FIDELEN_ICONS = {
  grid: {
    r: [
      [3, 3, 7, 7, 1],
      [14, 3, 7, 7, 1],
      [3, 14, 7, 7, 1],
      [14, 14, 7, 7, 1],
    ],
  },
  document: { d: ['M9 8h6M9 12h6'], r: [[4, 3, 16, 18, 2]] },
  confirm: { d: ['M20 6L9 17l-5-5'], w: 2.4 },
  order: { d: ['M21 8l-9-5-9 5 9 5 9-5z', 'M3 8v8l9 5 9-5V8'] },
  package: { d: ['M3 10h18'], r: [[3, 4, 18, 16, 2]] },
  rfq: { d: ['M20 12l-8 8-9-9V3h8z'], c: [[7.5, 7.5, 1.3]] },
  quote: {
    d: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6M9 13h6M9 17h4'],
  },
  vendor: { d: ['M3 9l1.8-5h14.4L21 9', 'M5 9v11h14V9', 'M9 20v-6h6v6'] },
  savings: { d: ['M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'] },
  user: { d: ['M4 21c0-4 3.6-6 8-6s8 2 8 6'], c: [[12, 8, 4]] },
  lock: { d: ['M8 11V7a4 4 0 0 1 8 0v4'], r: [[4, 11, 16, 10, 2]], w: 2 },
  settings: {
    d: [
      'M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7.5 19l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 3 15H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 5 9.5L4.9 9.4a2 2 0 1 1 2.8-2.8l.1.1A1.6 1.6 0 0 0 10.5 6V6a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.8 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7H21a2 2 0 1 1 0 4z',
    ],
    c: [[12, 12, 3]],
  },
  download: { d: ['M12 3v12M8 11l4 4 4-4M5 21h14'] },
  catalog: { d: ['M3 10h18'], r: [[3, 4, 18, 16, 2]] },
  // The field error's Critical mark, the search field's lead, a dialog's
  // close — the same sheet (final-layer1-mvp-2026-09-09 Icon.jsx), unchanged.
  search: { d: ['m21 21-4.3-4.3'], c: [[11, 11, 7]] },
  alert: { d: ['M12 8v4M12 16h.01'], c: [[12, 12, 9]], w: 1.9 },
  close: { d: ['M18 6 6 18M6 6l12 12'], w: 2 },
  // Lucide `circle-help`, the same sheet's source: the Help & tours entry.
  help: { d: ['M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3', 'M12 17h.01'], c: [[12, 12, 10]] },
  // Lucide `chevron-right`: a disclosure's marker (mirrored in Arabic by its user).
  chevron: { d: ['m9 18 6-6-6-6'] },
  // The date field's affordance (R-050 v1.1, added with `DateInput`; the sheet's
  // never-mirror list names it): the same path data as the design system's Icon.jsx.
  calendar: { d: ['M8 2v4M16 2v4M3 10h18'], r: [[3, 4, 18, 18, 2]] },
  // Lucide `triangle-alert`, the glyph R-050's MetricTile names for the warning
  // tone (R-050 takes its glyphs from Lucide; the circle `alert` is exception's).
  warning: {
    d: [
      'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3',
      'M12 9v4',
      'M12 17h.01',
    ],
    w: 1.9,
  },
} satisfies Record<string, Glyph>;

export type IconName = keyof typeof FIDELEN_ICONS;

export function Icon({
  name,
  size = 17,
  className,
}: {
  name: IconName;
  size?: number;
  className?: string;
}) {
  const g: Glyph = FIDELEN_ICONS[name];

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={g.w ?? 1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      // Decorative: every icon in this product sits beside its own text label,
      // so announcing it would read the same thing twice.
      aria-hidden="true"
      focusable="false"
    >
      {g.d?.map((d) => (
        <path key={d} d={d} />
      ))}
      {g.c?.map(([cx, cy, r]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} />
      ))}
      {g.r?.map(([x, y, w, h, r]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width={w} height={h} rx={r} />
      ))}
    </svg>
  );
}
